import { constants } from 'src/environments/constants';

/**
 * Récupère {@link RouteActivityType} pour un code {@link TypeActivite}.
 * @param activityType Code {@link TypeActivite} pour lequel on souhaite récupérer son {@link RouteActivityType}
 * @private
 */
export function getRouteActivityTypeFromActivityType(activityType: TypeActivite): RouteActivityType | undefined {
    switch (activityType) {
        case TypeActivite.Vente:
        case TypeActivite.ChequesEtBons:
            return RouteActivityType.Sales;
        case TypeActivite.Prestation:
            return RouteActivityType.Benefits;
        case TypeActivite.Groupe:
            return RouteActivityType.Groups;
        case TypeActivite.Location:
            return RouteActivityType.Rentals;
        case TypeActivite.Epargnes:
            return RouteActivityType.Savings;
        case TypeActivite.Mediatheque:
            return RouteActivityType.MediaLibraries;
        case TypeActivite.Pret:
            return RouteActivityType.Loans;
        case TypeActivite.RessourcesPartagees:
            return RouteActivityType.SharedResources;
        case TypeActivite.Cotisation:
            return RouteActivityType.Contributions;
        default:
            return undefined;
    }
}

/**
 * Paramètre de routing utilisé pour définir le type d'activité en cours d'utilisation
 */
export enum RouteActivityType {
    General = 'general-settings', // Paramétrage général
    Sales = 'sales', // Ventes et Chèques
    Benefits = 'benefits', // Prestation
    Rentals = 'rentals', // Locations
    MediaLibraries = 'media-libraries', // Médiathèque
    Loans = 'loans', // Prêt et dons
    Groups = 'groups', // Groupes
    Savings = 'savings', // Epargnes
    Contributions = 'contributions', // Cotisation
    SharedResources = 'shared-resources', // Ressources partagées
}

export enum TypeActivite {
    Vente = 'VEN',
    Location = 'LOC',
    ChequesEtBons = 'CHQ',
    Prestation = 'PRS',
    Mediatheque = 'MDK',
    Pret = 'PRT',
    Groupe = 'GRP',
    Cotisation = 'COT',
    RessourcesPartagees = 'RES',
    Epargnes = 'EPA'
}

export enum TypeReglement {
    Aucun,
    Beneficiaire = 'ReglementClient',
    Fournisseur = 'ReglementFournisseur',
    RemboursementAvoirClient = 'RemboursementAvoirClient',
    RemboursementAvoirFournisseur = 'RemboursementAvoirFournisseur',
    AcompteFournisseur = 'AcompteFournisseur',
    AcompteClient = 'AcompteClient',
    ReglementPretBenef = 'ReglementPretBenef',
    RemboursementPretBenef = 'RemboursementPretBenef',
    DonCEBenef = 'DonCEBenef'
}

export enum MoyensPaiement {
    Especes = 'ESP',
    CarteBancaire = 'CBA',
    ChequeBancaire = 'CHQ',
    ChequeVacances = 'CHV',
    VirementSEPASurCompte = 'VIB',
    VirementManuel = 'VIM',
    VirementInterne = 'VII',
    VirementSalaire = 'VIS',
    PrelevementManuelSurCompte = 'PRM',
    PrelevementSEPASurCompte = 'PRB',
    PrelevementSurSalaire = 'PRS',
    PorteMonnaieVirtuel = 'PMV',
    CompteCourantFournisseur = 'CCF',
    EspecesEtAutres = 'E&A',
}

export enum PaymentMode {
    None,
    SimpleMode,
    AdvancedMode,
    LaterMode
}

export enum TypeJournal {
    EspecesEtAutres = 'E&A',
    Tresorerie = 'TRS',
    OuvrantDroit = 'OD',
    Anouveau = 'RAN',
    Achat = 'HA',
    Vente = 'VT',
    Od = 'OD',
    Remboursement = 'RBT'
}

export enum CollectiveContext {
    Collective,
    DepositCollective
}

export enum ContexteAcompte {
    Fournisseur = 1,
    Client,
    RemboursementAnticipePretsEtDons,
    ReglementManuelEpargne,
    Generique,
}

export const AdministrativeStatus = {
    Invoiceable: 'FAA',
    Refused: 'RFC',
    Canceled: 'ANC',
    Waiting: 'ATC',
    Returned: 'RET',
    PartiallyReturned: 'REP',
    CanceledByBenef: 'ANB',
    Invoiced: 'FAC',
    InvoicedNotSettled: 'FAC-NSO',
    PartiallyInvoiced: 'FAP',
    NotDeliverable: 'NLI',
    NotDelivered: 'LIA',
    PartiallyDelivered: 'LIP',
    Delivered: 'LIV',
    NotApplicable: 'SOB',
    BeingEntered: 'SAI',
    Sent: 'ENV'
};

export type ListeCodeStatutsLivraisonType = { code: string, label: string, class?: string, supplierLabel?: string };
export const ListeCodeStatutsLivraison: ListeCodeStatutsLivraisonType[] = [
    {
        code: AdministrativeStatus.NotDeliverable,
        label: 'Non livrable',
        supplierLabel: 'Non réceptionnée',
        class: 'gup-U-color--red'
    },
    {
        code: AdministrativeStatus.NotDelivered,
        label: 'Non livrée',
        supplierLabel: 'À réceptionner',
        class: 'gup-U-color--orange'
    },
    {
        code: AdministrativeStatus.PartiallyDelivered,
        label: 'Livrée partiellement',
        supplierLabel: 'Réceptionnée partiellement',
        class: 'gup-U-color--yellow'
    },
    {
        code: AdministrativeStatus.Delivered,
        label: 'Livrée',
        supplierLabel: 'Réceptionnée',
        class: 'gup-U-color--green'
    },
    {
        code: AdministrativeStatus.NotApplicable,
        label: 'Sans objet'
    },
    {
        code: AdministrativeStatus.Returned,
        label: 'Retournée'
    }
];

export const ListeStatutsFacture: { code: string, label: string, class?: string }[] = [
    {
        code: constants.facture.statut.SOLDE,
        label: 'Soldée',
        class: 'gup-U-color--green'
    },
    {
        code: constants.facture.statut.PARTIELLEMENT_SOLDE,
        label: 'Partiellement soldée',
        class: 'gup-U-color--yellow'
    },
    {
        code: constants.facture.statut.NON_SOLDE,
        label: 'Non soldée',
        class: 'gup-U-color--orange'
    },
];

export type ListeStatutsDossierType = { code: string, label: string, class?: string };
export const ListeStatutsDossier: ListeStatutsDossierType[] = [
    {
        code: AdministrativeStatus.Waiting,
        label: 'En attente de validation',
        class: 'gup-U-color--orange'
    },
    {
        code: AdministrativeStatus.BeingEntered,
        label: 'Saisie en cours',
        class: 'gup-U-color--orange'
    },
    {
        code: AdministrativeStatus.Invoiceable,
        label: 'Acceptée',
        class: 'gup-U-color--blue'
    },
    {
        code: AdministrativeStatus.Invoiced,
        label: 'Facturée',
        class: 'gup-U-color--green'
    },
    {
        code: AdministrativeStatus.Refused,
        label: 'Refusée',
        class: 'gup-U-color--red'
    },
    {
        code: AdministrativeStatus.Canceled,
        label: 'Annulée',
        class: 'gup-U-color--400'
    },
    {
        code: AdministrativeStatus.CanceledByBenef,
        label: 'Annulée',
        class: 'gup-U-color--400'
    },
    {
        code: AdministrativeStatus.Canceled,
        label: 'Annulée CSE',
        class: 'gup-U-color--400'
    },
    {
        code: AdministrativeStatus.PartiallyInvoiced,
        label: 'Facturée partiellement',
        class: 'gup-U-color--yellow'
    }
];

export enum ActivityBenefitsType {
    Cadeau = 'CAD',
    Remboursement = 'RBS',
    RemboursementMensuel = 'MSL',
    PartACharge = 'PAC',
}

export const getBenefitsType = (type: string): string => {
    switch (type) {
        case ActivityBenefitsType.Remboursement:
            return 'Remboursement';
        case ActivityBenefitsType.RemboursementMensuel:
            return 'Remboursement (mensuel)';
        case ActivityBenefitsType.Cadeau:
            return 'Cadeaux';
        case ActivityBenefitsType.PartACharge:
            return 'Part à charge';
        default:
            return '';
    }
};

export const ExerciceStatus = {
    ClosingInProgress: 'Clôture en cours',
    Closed: 'Clôturée'
};

export const RegistrationsStatusList: { value?: string, label: string }[] = [
    {value: 'IsStatutTraitee', label: 'Traitées'},
    {value: 'IsStatutEnCours', label: 'En Cours'},
    {value: 'IsStatutImpayee', label: 'Impayées'},
    {value: 'IsStatutRefusee', label: 'Refusées par le CSE'},
    {value: 'IsStatutAnnuleCommande', label: 'Annulées par le CSE'},
];

export const OriginsList: { value?: boolean, label: string }[] = [
    {value: null, label: 'Toutes'},
    {value: true, label: 'En ligne'},
    {value: false, label: 'Gestion'},
];

export const originsList = [
    {value: 'all', label: 'Tous'},
    {value: 'gestion', label: 'Logiciel'},
    {value: 'online', label: 'Site'},
];

export enum ActivityLoansType {
    Dons = 'D',
    Prets = 'P'
}

export enum ActivityLoansCode {
    Dons = 'DON',
    Prets = 'PRET'
}

export enum accountingMovementDirection {
    Debit = 'D',
    Credit = 'C'
}

export enum TypeFacture {
    FactureClient = 'C',
    FactureFournisseur = 'F'
}
